import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import to from 'await-to-js'

import Checkbox from 'components/Checkbox/checkbox'
import FbButton from 'components/FbUI/FbButton'
import { Switch } from 'components/UI/Switch/Switch'
import {
  AutoUnsubscribeMode,
  AutoUnsubscribePattern,
  useGetGlobalCampaignSettings,
  useUpdateGlobalCampaignSettings,
} from 'features/campaigns/global_settings_api'

import {
  arePatternListsEqual,
  findDuplicateError,
  validateRow,
} from './autoUnsubscribeValidation'

const MODE_OPTIONS: { value: AutoUnsubscribeMode; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'whole_reply', label: 'Whole reply' },
  { value: 'regex', label: 'Regex' },
]

// Rows carry a client-only key so React can track identity across edits and
// potential future reorder. The key is stripped before sending to the backend.
type PatternRow = AutoUnsubscribePattern & { _key: string }

const stripKey = (row: PatternRow): AutoUnsubscribePattern => ({
  pattern: row.pattern,
  mode: row.mode,
  enabled: row.enabled,
})

const makeKey = (): string => crypto.randomUUID()

const toRows = (items: AutoUnsubscribePattern[]): PatternRow[] =>
  items.map((p) => ({ ...p, _key: makeKey() }))

export function AutoUnsubscribeSettings() {
  const { data: settings } = useGetGlobalCampaignSettings()
  const updateMutation = useUpdateGlobalCampaignSettings()

  // Memoize so identity is stable across renders (lets downstream useMemo
  // dependencies short-circuit when settings is unchanged).
  const serverPatterns = useMemo(
    () => settings?.auto_unsubscribe_patterns ?? [],
    [settings?.auto_unsubscribe_patterns]
  )
  const serverEnabled = settings?.auto_unsubscribe_enabled ?? true
  const defaultPatterns = useMemo(
    () => settings?.default_auto_unsubscribe_patterns ?? [],
    [settings?.default_auto_unsubscribe_patterns]
  )

  const [patternsDraft, setPatternsDraft] = useState<PatternRow[] | null>(null)
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null)

  // Server rows re-keyed only when the server payload changes — safe because
  // we only render by key while the user hasn't started editing (draft is null).
  const serverRows = useMemo(() => toRows(serverPatterns), [serverPatterns])
  const patterns: PatternRow[] = patternsDraft ?? serverRows
  const enabled = enabledDraft ?? serverEnabled

  const plainPatterns = useMemo(() => patterns.map(stripKey), [patterns])
  const rowErrors = useMemo(
    () => plainPatterns.map(validateRow),
    [plainPatterns]
  )
  const duplicateError = useMemo(
    () => findDuplicateError(plainPatterns),
    [plainPatterns]
  )

  const hasErrors = rowErrors.some(Boolean) || Boolean(duplicateError)
  const patternsDirty =
    patternsDraft !== null &&
    !arePatternListsEqual(plainPatterns, serverPatterns)
  const enabledDirty = enabledDraft !== null && enabledDraft !== serverEnabled
  const isDirty = patternsDirty || enabledDirty

  const updateRow = (
    index: number,
    partial: Partial<AutoUnsubscribePattern>
  ) => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return base.map((row, i) => (i === index ? { ...row, ...partial } : row))
    })
  }

  const removeRow = (index: number) => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return base.filter((_, i) => i !== index)
    })
  }

  const addRow = () => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return [
        ...base,
        { pattern: '', mode: 'contains', enabled: true, _key: makeKey() },
      ]
    })
  }

  const restoreDefaults = () => {
    setPatternsDraft(toRows(defaultPatterns))
  }

  const handleSave = async () => {
    if (hasErrors || !isDirty) return
    const payload: {
      auto_unsubscribe_enabled?: boolean
      auto_unsubscribe_patterns?: AutoUnsubscribePattern[]
    } = {}
    if (patternsDirty) payload.auto_unsubscribe_patterns = plainPatterns
    if (enabledDirty) payload.auto_unsubscribe_enabled = enabled

    const [err] = await to(updateMutation.mutateAsync(payload))
    if (err) {
      const fallback = 'Failed to save auto-unsubscribe settings'
      const detail = axios.isAxiosError<{ detail?: string }>(err)
        ? err.response?.data?.detail
        : undefined
      toast.error(detail ?? fallback)
      return
    }

    setPatternsDraft(null)
    setEnabledDraft(null)
    toast.success('Auto-unsubscribe settings saved')
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-fg">
              Reply opt-out detection
            </h4>
            <span className="text-xxs font-medium uppercase tracking-wider text-fg-secondary bg-surface-hover px-1.5 py-0.5 rounded">
              Inbound
            </span>
          </div>
          <p className="text-xs text-fg-secondary mt-0.5">
            When a contact replies with any of the phrases below, unsubscribe
            them in EmailBison and mark them as Do Not Contact here.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-0.5">
          <span className="text-xxs text-fg-secondary">
            {enabled ? 'On' : 'Off'}
          </span>
          <Switch
            aria-label="Enable reply opt-out detection"
            checked={enabled}
            onChange={(e) => setEnabledDraft(e.target.checked)}
          />
        </div>
      </div>

      <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
        {patterns.length > 0 && (
          <div className="grid grid-cols-[auto_140px_1fr_auto] gap-2 items-center mb-1 px-1">
            <span className="text-xxs text-fg-secondary uppercase tracking-wider">
              On
            </span>
            <span className="text-xxs text-fg-secondary uppercase tracking-wider">
              Match type
            </span>
            <span className="text-xxs text-fg-secondary uppercase tracking-wider">
              Phrase or pattern
            </span>
            <span className="sr-only">Actions</span>
          </div>
        )}

        <div className="space-y-2">
          {patterns.length === 0 && (
            <p className="text-xs text-fg-secondary italic py-2">
              No rules configured. Add one below or restore the recommended
              defaults.
            </p>
          )}

          {patterns.map((row, index) => (
            <div
              key={row._key}
              className="grid grid-cols-[auto_140px_1fr_auto] gap-2 items-start"
            >
              <Checkbox
                aria-label="Rule enabled"
                checked={row.enabled}
                onChange={(e) =>
                  updateRow(index, { enabled: e.target.checked })
                }
                wrapperClassName="mt-2.5"
              />
              <select
                aria-label="Match type"
                value={row.mode}
                onChange={(e) =>
                  updateRow(index, {
                    mode: e.target.value as AutoUnsubscribeMode,
                  })
                }
                className="h-9 px-2 text-sm border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              >
                {MODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div>
                <input
                  type="text"
                  aria-label="Phrase or pattern"
                  value={row.pattern}
                  placeholder="e.g. unsubscribe"
                  maxLength={200}
                  onChange={(e) =>
                    updateRow(index, { pattern: e.target.value })
                  }
                  className="w-full h-9 px-3 text-sm border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                />
                {rowErrors[index] && (
                  <p className="text-xxs text-error mt-1">{rowErrors[index]}</p>
                )}
              </div>
              <button
                type="button"
                aria-label="Remove rule"
                onClick={() => removeRow(index)}
                className="h-9 px-2 text-fg-secondary hover:text-error"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {duplicateError && (
        <p className="text-xxs text-error mt-2">{duplicateError}</p>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-line">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1.5 text-xs text-brand hover:underline"
          >
            <Plus className="size-3" />
            Add rule
          </button>
          <button
            type="button"
            onClick={restoreDefaults}
            className="flex items-center gap-1.5 text-xs text-brand hover:underline"
          >
            <RotateCcw className="size-3" />
            Restore defaults
          </button>
        </div>
        <FbButton
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || hasErrors || updateMutation.isPending}
          loading={updateMutation.isPending}
        >
          Save
        </FbButton>
      </div>
    </div>
  )
}
